import { useState } from "react";
import { ArrowLeft, GitPullRequest, Loader2, Plus } from "lucide-react";
import { useGitBranches, useCreatePullRequest } from "@/hooks/git";
import type { GitPullRequestDetail } from "@/services/git/types";
import { CustomSelect } from "@/components/atoms/CustomSelect";

// ─── Create PR Pane ───────────────────────────────────────────────────────────

export function CreatePrPane({
  repoFullName,
  defaultBranch,
  onSuccess,
  onCancel,
}: {
  repoFullName: string;
  defaultBranch: string;
  onSuccess: (pr: GitPullRequestDetail) => void;
  onCancel: () => void;
}) {
  const [title, setTitle] = useState("");
  const [head, setHead] = useState("");
  const [base, setBase] = useState(defaultBranch);
  const [body, setBody] = useState("");
  const [error, setError] = useState<string | null>(null);

  const { data: branches = [], isLoading: branchesLoading } = useGitBranches(repoFullName);
  const { mutate: create, isPending: creating } = useCreatePullRequest();

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!title.trim() || !head) return;
    create(
      { repo_full_name: repoFullName, title: title.trim(), head, base, body: body.trim() || undefined },
      {
        onSuccess: (pr) => onSuccess(pr),
        onError: (err) => setError(err.message),
      },
    );
  }

  return (
    <div className="flex h-full flex-col overflow-y-auto p-5">
      <div className="mb-5">
        <div className="mb-1 flex items-center gap-2">
          <GitPullRequest className="h-4 w-4 text-primary" />
          <h2 className="text-sm font-semibold">Create Pull Request</h2>
        </div>
        <p className="text-xs text-muted-foreground">Open a new pull request on GitHub.</p>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div>
          <label className="mb-1.5 block text-xs font-medium">
            Title <span className="text-destructive">*</span>
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="feat: add new feature"
            autoFocus
            className="w-full rounded-lg border border-border bg-muted/30 px-3 py-2 text-xs outline-none placeholder:text-muted-foreground/50 focus:border-primary/60 focus:ring-1 focus:ring-primary/20"
          />
        </div>

        <div className="flex items-end gap-2">
          <div className="flex-1">
            <label className="mb-1.5 block text-xs font-medium">
              base <span className="text-destructive">*</span>
            </label>
            <CustomSelect
              value={base}
              onChange={setBase}
              options={branches.map((b) => ({ value: b.name, label: b.name }))}
              disabled={branchesLoading}
              mono
            />
          </div>
          <div className="flex shrink-0 items-center" style={{ height: 36, marginBottom: 0 }}>
            <ArrowLeft className="h-3.5 w-3.5 text-muted-foreground" />
          </div>
          <div className="flex-1">
            <label className="mb-1.5 block text-xs font-medium">
              compare <span className="text-destructive">*</span>
            </label>
            <CustomSelect
              value={head}
              onChange={setHead}
              options={branches.map((b) => ({ value: b.name, label: b.name }))}
              disabled={branchesLoading}
              placeholder="Select…"
              mono
            />
          </div>
        </div>

        <div>
          <label className="mb-1.5 block text-xs font-medium">
            Description <span className="text-muted-foreground">(optional)</span>
          </label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={"## Summary\n- Added new feature"}
            rows={5}
            className="w-full resize-none rounded-lg border border-border bg-muted/30 px-3 py-2 text-xs outline-none placeholder:text-muted-foreground/50 focus:border-primary/60 focus:ring-1 focus:ring-primary/20"
          />
        </div>

        {error && (
          <p className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive">
            {error}
          </p>
        )}

        <div className="flex gap-2 pt-1">
          <button
            type="submit"
            disabled={creating || !title.trim() || !head}
            className="studio-button studio-button-primary flex-1 justify-center"
          >
            {creating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
            {creating ? "Creating…" : "Create PR"}
          </button>
          <button type="button" onClick={onCancel} className="studio-button">
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
